/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  RefreshCw, Check, X as XIcon, Repeat, ExternalLink, Calendar,
  CheckCircle2, XCircle, Circle, Power, Eye, Flame, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { PickAnalysisTriggerBtn } from '../components/PickAnalysisDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform visuals (shared with /admin/publisher)
const PLATFORM_CHIP = {
  instagram:     'bg-[#FF006E]/20 text-pink-300 border-pink-500/40',
  tiktok:        'bg-black text-zinc-200 border-zinc-600',
  telegram_free: 'bg-[#0088CC]/20 text-sky-300 border-sky-500/40',
  telegram_vip:  'bg-[#FFBE0B]/15 text-amber-300 border-amber-500/40',
};
const PLATFORM_LABEL = {
  instagram: 'IG', tiktok: 'TikTok', telegram_free: 'TG Free', telegram_vip: 'TG VIP',
};
const STATUS_CHIP = {
  scheduled: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  publishing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  blocked_not_approved: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  skipped_tiktok: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};

function adminHeaders() {
  const k = localStorage.getItem('admin_key') || '';
  return { 'X-Admin-Key': k };
}

function dayKey(iso) {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dday = new Date(d); dday.setHours(0, 0, 0, 0);
  if (dday.getTime() === today.getTime()) return 'Hoy';
  if (dday.getTime() === tomorrow.getTime()) return 'Mañana';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' });
}
function fmtCET(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}


// =====================================================================
// HEADER — connection badges + dual autopublish toggles
// =====================================================================
function HeaderHealth({ health, onFlip }) {
  if (!health) return <div className="text-zinc-500 text-sm">Cargando estado…</div>;
  const tg = health.telegram || { free: {}, vip: {} };
  const metaOn = health.autopublish?.meta?.enabled;
  const tgOn = health.autopublish?.telegram?.enabled;

  const Badge2 = ({ label, ok, sub, color }) => (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
      <div className="leading-tight">
        <div className="text-xs font-semibold text-white">{label}</div>
        <div className={`text-[10px] ${ok ? 'text-emerald-300' : 'text-rose-300'}`}>{sub}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge2 label="Instagram" ok={health.meta?.connected}
                sub={health.meta?.connected ? `@${health.meta.ig_username}` : 'Desconectado'}
                color="bg-[#FF006E]" />
        <Badge2 label="TikTok" ok={false} sub="Sprint 4b" color="bg-zinc-600" />
        <Badge2 label="TG Free" ok={tg.free?.connected}
                sub={tg.free?.connected ? `@${tg.free.bot_username}` : 'Sin bot'}
                color="bg-[#0088CC]" />
        <Badge2 label="TG VIP" ok={tg.vip?.connected}
                sub={tg.vip?.connected ? `@${tg.vip.bot_username}` : 'Sin bot'}
                color="bg-[#FFBE0B]" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className={metaOn ? "bg-emerald-950/30 border-emerald-500/30" : "bg-rose-950/20 border-rose-500/20"}>
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
                <Power className="w-3 h-3" /> Autopublish IG + TikTok
              </div>
              <div className={`text-base font-bold ${metaOn ? 'text-emerald-300' : 'text-rose-300'}`}>
                {metaOn ? 'ACTIVADO' : 'DESACTIVADO'}
              </div>
            </div>
            <Button size="sm"
              className={metaOn ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
              onClick={() => onFlip('meta', !metaOn)}
              data-testid="pub-flip-meta-btn"
            >
              {metaOn ? 'OFF' : 'ON'}
            </Button>
          </CardContent>
        </Card>
        <Card className={tgOn ? "bg-emerald-950/30 border-emerald-500/30" : "bg-rose-950/20 border-rose-500/20"}>
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
                <Power className="w-3 h-3" /> Autopublish Telegram
              </div>
              <div className={`text-base font-bold ${tgOn ? 'text-emerald-300' : 'text-rose-300'}`}>
                {tgOn ? 'ACTIVADO' : 'DESACTIVADO'}
              </div>
            </div>
            <Button size="sm"
              className={tgOn ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
              onClick={() => onFlip('telegram', !tgOn)}
              data-testid="pub-flip-tg-btn"
            >
              {tgOn ? 'OFF' : 'ON'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// =====================================================================
// SECTION 1 — Timeline próximas 48h
// =====================================================================
function TimelineSection() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/queue?hours=48`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { toast.error(`Timeline: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  async function cancel(id) {
    if (!window.confirm('¿Cancelar esta publicación?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/cancel/${id}`, { method: 'POST', headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Cancelada'); load();
    } catch (e) { toast.error(e.message); }
  }

  const grouped = useMemo(() => {
    const by = {};
    for (const it of data.items || []) {
      const k = dayKey(it.scheduled_at);
      (by[k] ||= []).push(it);
    }
    return by;
  }, [data]);

  return (
    <section data-testid="section-timeline">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white">Hoy y mañana</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="timeline-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </Button>
      </div>
      {Object.keys(grouped).length === 0 && (
        <div className="text-zinc-500 text-center py-8 border border-white/5 rounded-lg bg-zinc-900/40">
          Sin publicaciones en las próximas 48h.
        </div>
      )}
      {Object.entries(grouped).map(([dayLabel, rows]) => (
        <div key={dayLabel} className="mb-5">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-zinc-400">
            <Calendar className="w-3.5 h-3.5" /> {dayLabel} <span className="text-zinc-600">· {rows.length}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {rows.map((it) => (
              <Card key={it.id} className="bg-zinc-900/60 border-white/5 flex-shrink-0 w-[260px]">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-200 font-mono text-sm">{fmtCET(it.scheduled_at)}</span>
                    <Badge variant="outline" className={PLATFORM_CHIP[it.platform]}>{PLATFORM_LABEL[it.platform] || it.platform}</Badge>
                  </div>
                  <div className="w-full h-36 bg-black rounded overflow-hidden">
                    {it.thumbnail_url && (
                      <video src={it.thumbnail_url} preload="metadata" muted className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400">{it.template} · {it.mood}</div>
                  <div className="text-[11px] text-zinc-300 line-clamp-2 min-h-[2em]">{it.caption_preview || '—'}</div>
                  <div className="flex items-center gap-1.5 pt-1">
                    <Badge variant="outline" className={`${STATUS_CHIP[it.status]} text-[10px]`}>{it.status}</Badge>
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => cancel(it.id)}
                      data-testid={`timeline-cancel-${it.id}`}
                      className="text-rose-300 hover:text-rose-200 text-xs h-7 px-2">
                      <XIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}


// =====================================================================
// SECTION 2 — Cola pendiente de revisar
// =====================================================================
function PendingReviewSection() {
  const [items, setItems] = useState([]);
  const [focusIndex, setFocusIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/content/reels?status=pending_review&limit=30`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
      setFocusIndex((i) => Math.min(i, Math.max(0, (d.items || []).length - 1)));
    } catch (e) { toast.error(`Pendientes: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  async function doApprove(item) {
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${item.id}/approve`, { method: 'POST', headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Reel aprobado'); load();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  }
  async function doRegenerate(item) {
    if (!window.confirm('¿Regenerar este reel? Se archivará y se creará un nuevo job.')) return;
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${item.id}/regenerate`, { method: 'POST', headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Regeneración encolada'); load();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  }
  function askReject(item) { setRejectTarget(item); setRejectReason(''); setRejectOpen(true); }
  async function confirmReject() {
    if (!rejectTarget) return;
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${rejectTarget.id}/reject`, {
        method: 'POST', headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Reel rechazado'); setRejectOpen(false); load();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  }

  // Hotkeys A / R / X / ← →
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (!items.length) return;
      const cur = items[focusIndex];
      if (!cur) return;
      if (e.key === 'a' || e.key === 'A') { doApprove(cur); e.preventDefault(); }
      else if (e.key === 'r' || e.key === 'R') { doRegenerate(cur); e.preventDefault(); }
      else if (e.key === 'x' || e.key === 'X') { askReject(cur); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { setFocusIndex((i) => Math.min(items.length - 1, i + 1)); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { setFocusIndex((i) => Math.max(0, i - 1)); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, focusIndex]);

  return (
    <section data-testid="section-pending">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white">
          Pendientes de revisar <span className="text-zinc-400 font-normal">({items.length})</span>
        </h2>
        <div className="text-xs text-zinc-500">
          Atajos: <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded">A</kbd> aprobar ·{' '}
          <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded">R</kbd> regen ·{' '}
          <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded">X</kbd> rechazar ·{' '}
          <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded">←/→</kbd> navegar
        </div>
      </div>
      {loading && items.length === 0 && <div className="text-zinc-400 text-center py-6">Cargando…</div>}
      {!loading && items.length === 0 && (
        <div className="text-zinc-500 text-center py-8 border border-white/5 rounded-lg bg-zinc-900/40">
          🎉 Nada pendiente. Todos los reels están revisados.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it, idx) => (
          <Card key={it.id}
            className={`bg-zinc-900/70 border transition-all ${idx === focusIndex ? 'border-emerald-400 shadow-[0_0_0_2px_rgba(52,211,153,0.35)]' : 'border-white/5 hover:border-white/15'}`}
            data-testid={`pending-reel-${it.id}`}
            onClick={() => setFocusIndex(idx)}
          >
            <CardContent className="p-3 space-y-2">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-[9/16]">
                <video src={it.file_url} controls preload="metadata" playsInline
                       className="w-full h-full object-contain" />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="bg-white/5 text-zinc-200 border-white/10 text-[10px]">{it.template}</Badge>
                <Badge variant="outline" className="bg-white/5 text-zinc-300 border-white/10 text-[10px]">{it.mood}</Badge>
                <Badge variant="outline" className="bg-white/5 text-zinc-300 border-white/10 text-[10px]">
                  <Clock className="w-2.5 h-2.5 mr-1" /> {it.duration_s}s
                </Badge>
              </div>
              <div className="text-[11px] text-zinc-300 line-clamp-2">{it.copy_data?.caption_ig || it.copy_data?.subtitle}</div>
              {it.pick_id && (
                <div className="pt-0.5">
                  <PickAnalysisTriggerBtn
                    pickId={it.pick_id}
                    matchLabel={it.copy_data?.title || it.copy_data?.subtitle || 'Pick'}
                    className="w-full h-8"
                  />
                </div>
              )}
              <div className="flex items-center gap-1.5 pt-1">
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                  onClick={(e) => { e.stopPropagation(); doApprove(it); }}
                  data-testid={`pending-approve-${it.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Aprobar
                </Button>
                <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 h-8"
                  onClick={(e) => { e.stopPropagation(); doRegenerate(it); }}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
                  onClick={(e) => { e.stopPropagation(); askReject(it); }}>
                  <XIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>Motivo del rechazo</DialogTitle></DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ej: caption confuso, mood mal encajado, demasiado largo…"
            className="bg-zinc-900 border-white/10 text-white" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-500 text-white" onClick={confirmReject}>Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


// =====================================================================
// SECTION 3 — Historial 7d con views + top templates
// =====================================================================
function HistorySection() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState('all');
  const [template, setTemplate] = useState('all');
  const [top, setTop] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailSeries, setDetailSeries] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ days: '7', status: 'published' });
      if (platform !== 'all') qs.set('platform', platform);
      if (template !== 'all') qs.set('template', template);
      const r = await fetch(`${API_URL}/api/admin/publisher/history?${qs}`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { toast.error(`Historial: ${e.message}`); }
    finally { setLoading(false); }
  }, [platform, template]);

  const loadTop = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/metrics/top-templates?days=7&limit=3`, { headers: adminHeaders() });
      if (r.ok) setTop((await r.json()).items || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => { load(); loadTop(); }, [load, loadTop]);

  // Fetch latest views for each post (join client-side)
  const [viewsMap, setViewsMap] = useState({});
  useEffect(() => {
    const ids = (data.items || []).filter((it) => it.platform?.startsWith('telegram')).map((it) => it.id);
    if (!ids.length) return;
    // Single-pass client joins — small N
    (async () => {
      const map = {};
      for (const id of ids) {
        try {
          const r = await fetch(`${API_URL}/api/admin/publisher/metrics/post/${id}`, { headers: adminHeaders() });
          if (r.ok) {
            const d = await r.json();
            const last = (d.snapshots || []).slice(-1)[0];
            if (last) map[id] = last;
          }
        } catch (_e) { /* ignore */ }
      }
      setViewsMap(map);
    })();
  }, [data]);

  async function openDetail(item) {
    setDetail(item);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/metrics/post/${item.id}`, { headers: adminHeaders() });
      if (r.ok) {
        const d = await r.json();
        setDetailSeries((d.snapshots || []).map((s) => ({
          t: new Date(s.snapshot_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
          views: s.views || 0,
          reactions: s.reactions_total || 0,
        })));
      } else {
        setDetailSeries([]);
      }
    } catch (_e) { setDetailSeries([]); }
  }

  return (
    <section data-testid="section-history">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white">Historial 7 días</h2>
        <Button variant="outline" size="sm" onClick={() => { load(); loadTop(); }} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {top.length > 0 && (
        <Card className="bg-zinc-900/60 border-white/5 mb-3">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
              <Flame className="w-3.5 h-3.5 text-amber-400" /> Top 3 templates por views/hora (7d)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {top.map((t, i) => (
                <div key={t.template} className="flex items-center gap-2 bg-black/30 rounded px-2 py-1.5 border border-white/5">
                  <span className="text-2xl font-bold text-amber-400">#{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-sm text-white font-semibold truncate">{t.template}</div>
                    <div className="text-[10px] text-zinc-400">{t.avg_vph} v/h · {t.sample} posts</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-40 bg-zinc-900 border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas plataformas</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="tiktok">TikTok</SelectItem>
            <SelectItem value="telegram_free">Telegram Free</SelectItem>
            <SelectItem value="telegram_vip">Telegram VIP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-44 bg-zinc-900 border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos templates</SelectItem>
            {['pick_reveal', 'win_recap', 'stats_card', 'preview_match', 'meme_fail', 'countdown'].map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(data.items || []).length === 0 ? (
        <div className="text-zinc-500 text-center py-8 border border-white/5 rounded-lg bg-zinc-900/40">
          Sin publicaciones en los últimos 7 días.
        </div>
      ) : (
        <div className="space-y-2">
          {(data.items || []).map((it) => {
            const m = viewsMap[it.id];
            const isTG = it.platform?.startsWith('telegram');
            return (
              <Card key={it.id} className="bg-zinc-900/60 border-white/5">
                <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                  <div className="text-zinc-300 font-mono text-xs w-28">
                    {new Date(it.updated_at).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <Badge variant="outline" className={PLATFORM_CHIP[it.platform]}>{PLATFORM_LABEL[it.platform] || it.platform}</Badge>
                  <div className="text-xs text-zinc-400 w-32">{it.template}</div>
                  <Badge variant="outline" className={STATUS_CHIP[it.status]}>{it.status}</Badge>
                  <div className="flex-1 min-w-0">
                    {it.permalink ? (
                      <a href={it.permalink} target="_blank" rel="noreferrer"
                         className="text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 text-sm truncate">
                        {it.permalink} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-zinc-500 text-xs">—</span>}
                  </div>
                  {isTG && (
                    <div className="flex items-center gap-1 text-xs text-zinc-300" data-testid={`history-views-${it.id}`}>
                      <Eye className="w-3 h-3" />
                      <span>{m?.views ?? '—'}</span>
                      {m?.reactions_total ? <span className="text-amber-300 ml-1">· {m.reactions_total}👍</span> : null}
                      <button
                        className="ml-2 text-[10px] underline text-sky-400 hover:text-sky-300"
                        onClick={() => openDetail(it)}
                        data-testid={`history-detail-${it.id}`}
                      >
                        gráfico →
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle>Evolución · {detail?.platform} · {detail?.template}</DialogTitle></DialogHeader>
          {detailSeries.length === 0 ? (
            <div className="text-zinc-500 text-sm py-8 text-center">
              Sin snapshots todavía.<br />
              <span className="text-xs">Ejecuta el collector o espera 30 min.</span>
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={detailSeries}>
                  <XAxis dataKey="t" stroke="#71717a" fontSize={11} />
                  <YAxis stroke="#71717a" fontSize={11} />
                  <Tooltip contentStyle={{ background: '#09090b', border: '1px solid rgba(255,255,255,0.1)' }} />
                  <Line type="monotone" dataKey="views" stroke="#38bdf8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="reactions" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}


// =====================================================================
// MAIN PAGE
// =====================================================================
export default function Publicaciones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [pendingScope, setPendingScope] = useState('meta');
  const [confirmToken, setConfirmToken] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState('');

  // Admin gate — treat any authenticated user as admin (current app design)
  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const loadHealth = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/health`, { headers: adminHeaders() });
      if (r.ok) setHealth(await r.json());
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { loadHealth(); const t = setInterval(loadHealth, 60000); return () => clearInterval(t); }, [loadHealth]);

  async function onFlip(scope, enabled) {
    setPendingScope(scope); setPendingEnabled(enabled);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/flag/autopublish`, {
        method: 'POST', headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, scope }),
      });
      const d = await r.json();
      setConfirmToken(d.confirm_token);
      setConfirmMsg(d.message || '');
      setConfirmOpen(true);
    } catch (e) { toast.error(e.message); }
  }
  async function confirmFlip() {
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/flag/autopublish`, {
        method: 'POST', headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: pendingEnabled, scope: pendingScope, confirm_token: confirmToken }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Autopublish ${pendingScope} → ${pendingEnabled ? 'ON' : 'OFF'}`);
      setConfirmOpen(false); loadHealth();
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 pb-16" data-testid="publicaciones-page">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Publicaciones</h1>
          <p className="text-zinc-400 text-sm mt-1">Qué se publica hoy en tus canales · IG · TikTok · Telegram</p>
        </div>

        <div className="mb-8">
          <HeaderHealth health={health} onFlip={onFlip} />
        </div>

        <Tabs defaultValue="timeline" className="mb-10">
          <TabsList className="bg-zinc-900 border border-white/5">
            <TabsTrigger value="timeline" data-testid="pub-tab-timeline">Hoy y mañana</TabsTrigger>
            <TabsTrigger value="pending" data-testid="pub-tab-pending">
              Pendientes{health?.counters_24h?.pending_review_assets ? ` (${health.counters_24h.pending_review_assets})` : ''}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="pub-tab-history">Historial 7d</TabsTrigger>
          </TabsList>
          <TabsContent value="timeline" className="mt-5"><TimelineSection /></TabsContent>
          <TabsContent value="pending" className="mt-5"><PendingReviewSection /></TabsContent>
          <TabsContent value="history" className="mt-5"><HistorySection /></TabsContent>
        </Tabs>
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>Confirma el cambio</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">{confirmMsg}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button
              className={pendingEnabled ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-rose-600 hover:bg-rose-500 text-white"}
              onClick={confirmFlip}
              data-testid="pub-confirm-final"
            >
              Confirmar {pendingEnabled ? 'ACTIVAR' : 'DESACTIVAR'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
